/**
 * Form used to create a leak. Used both:
 *  - in /portal/leaks (student self-report; no owner picker)
 *  - in /admin/leaks  (admin with owner picker + initial status)
 *
 * `mode` = "student" | "admin". On submit, calls the appropriate create endpoint
 * via the parent's `onCreated` callback which receives the created leak object.
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ImageSquare, Trash } from "@phosphor-icons/react";
import { leaksApi, leaksAdminApi } from "../../api/leaks";

const STATUS_OPTIONS = ["received", "in_progress", "success", "failure"];

export default function ReportLeakForm({ mode = "student", onCreated, onCancel }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [status, setStatus] = useState("received");
  const [students, setStudents] = useState([]);
  const [pendingImages, setPendingImages] = useState([]); // File objects
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (mode !== "admin") return;
    leaksAdminApi.listStudents().then(setStudents).catch(() => setStudents([]));
  }, [mode]);

  const onAddImage = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    setPendingImages((list) => [...list, ...files]);
  };

  const removePending = (idx) =>
    setPendingImages((list) => list.filter((_, i) => i !== idx));

  const submit = useCallback(
    async (e) => {
      e.preventDefault();
      setError("");
      const t1 = title.trim();
      const u1 = url.trim();
      if (!t1 || !u1) {
        setError(t("leaks.form.required"));
        return;
      }
      if (mode === "admin" && !ownerId) {
        setError(t("leaks.form.owner_required"));
        return;
      }
      setSaving(true);
      try {
        const payload = {
          title: t1,
          url: u1,
          description: description.trim(),
        };
        const created =
          mode === "admin"
            ? await leaksAdminApi.create({
                ...payload,
                owner_id: ownerId,
                status,
              })
            : await leaksApi.create(payload);

        // Upload images (sequentially to keep order)
        const uploader = mode === "admin" ? leaksAdminApi : leaksApi;
        for (const file of pendingImages) {
          await uploader.uploadImage(created.id, file);
        }
        onCreated?.(created);
      } catch (err) {
        setError(err?.message || t("leaks.form.error_generic"));
      } finally {
        setSaving(false);
      }
    },
    [
      title,
      url,
      description,
      ownerId,
      status,
      pendingImages,
      mode,
      onCreated,
      t,
    ],
  );

  return (
    <form onSubmit={submit} data-testid="report-leak-form" className="space-y-4">
      {mode === "admin" && (
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">
            {t("leaks.form.owner")}
          </label>
          <select
            data-testid="leak-form-owner"
            value={ownerId}
            onChange={(e) => setOwnerId(e.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
          >
            <option value="">{t("leaks.form.owner_placeholder")}</option>
            {students.map((s) => (
              <option key={s.id} value={s.id}>
                {s.first_name || s.email} ({s.email})
              </option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">
          {t("leaks.form.title")} *
        </label>
        <input
          data-testid="leak-form-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t("leaks.form.title_placeholder")}
          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
        />
      </div>

      <div>
        <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">
          {t("leaks.form.url")} *
        </label>
        <input
          data-testid="leak-form-url"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://"
          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
        />
      </div>

      <div>
        <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">
          {t("leaks.form.description")}
        </label>
        <textarea
          data-testid="leak-form-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          placeholder={t("leaks.form.description_placeholder")}
          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm resize-y"
        />
      </div>

      {mode === "admin" && (
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">
            {t("leaks.form.initial_status")}
          </label>
          <select
            data-testid="leak-form-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {t(`leaks.status.${s}`)}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Images picker */}
      <div>
        <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">
          {t("leaks.form.images")}
        </label>
        <div className="space-y-2">
          {pendingImages.map((file, i) => (
            <div
              key={`${file.name}-${i}`}
              className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2"
            >
              <ImageSquare size={18} weight="regular" className="text-slate-400" />
              <span className="flex-1 text-sm truncate">{file.name}</span>
              <button
                type="button"
                onClick={() => removePending(i)}
                className="p-1 text-red-600 hover:bg-red-50 rounded"
                aria-label={t("common.delete")}
              >
                <Trash size={14} />
              </button>
            </div>
          ))}
          <label className="block">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={onAddImage}
              data-testid="leak-form-image-input"
              className="hidden"
            />
            <span className="inline-flex items-center gap-2 rounded-full border border-dashed border-slate-300 px-4 py-2 text-xs font-medium text-slate-700 hover:border-slate-900 cursor-pointer">
              <ImageSquare size={14} />
              {t("leaks.form.add_image")}
            </span>
          </label>
        </div>
      </div>

      {error && (
        <p data-testid="leak-form-error" className="text-sm text-red-600">
          {error}
        </p>
      )}

      <div className="flex justify-end gap-2 pt-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-full px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
          >
            {t("common.cancel")}
          </button>
        )}
        <button
          type="submit"
          data-testid="leak-form-submit"
          disabled={saving}
          className="rounded-full bg-slate-900 px-5 py-2 text-sm font-medium text-white disabled:opacity-40"
        >
          {saving ? t("leaks.form.submitting") : t("leaks.form.submit")}
        </button>
      </div>
    </form>
  );
}
